/**
 * gradeEngine — single source of truth for letter-grade logic.
 *
 * The backend already grades drafts (team_draft_analytics.get_draft_grade) on a
 * 9-grade scale. Everything here mirrors those bands or derives grades from
 * data already present in API responses. No fabricated metrics: if the data
 * isn't there, the grade is null and the UI shows a dash.
 */

export type LetterGrade =
  | 'A+'
  | 'A'
  | 'B+'
  | 'B'
  | 'C+'
  | 'C'
  | 'D+'
  | 'D'
  | 'F';

export const GRADE_ORDER: LetterGrade[] = [
  'A+',
  'A',
  'B+',
  'B',
  'C+',
  'C',
  'D+',
  'D',
  'F',
];

/** Grade band colors — maps onto the tailwind `grade.*` tokens. */
export const GRADE_COLORS: Record<'a' | 'b' | 'c' | 'f', string> = {
  a: '#2FBF71', // Turf Green
  b: '#5B8DEF', // info blue
  c: '#E0A63F', // warning amber
  f: '#E05252', // danger red
};

export function gradeBand(grade: LetterGrade): 'a' | 'b' | 'c' | 'f' {
  if (grade === 'A+' || grade === 'A') return 'a';
  if (grade === 'B+' || grade === 'B') return 'b';
  if (grade === 'C+' || grade === 'C') return 'c';
  return 'f';
}

export function gradeToColor(grade: LetterGrade): string {
  return GRADE_COLORS[gradeBand(grade)];
}

/** Numeric GPA-style value, A+ = 4.3 down to F = 0. Used for composites. */
export function gradeToPoints(grade: LetterGrade): number {
  const points: Record<LetterGrade, number> = {
    'A+': 4.3,
    A: 4.0,
    'B+': 3.3,
    B: 3.0,
    'C+': 2.3,
    C: 2.0,
    'D+': 1.3,
    D: 1.0,
    F: 0.0,
  };
  return points[grade];
}

export function pointsToGrade(points: number): LetterGrade {
  if (points >= 4.15) return 'A+';
  if (points >= 3.65) return 'A';
  if (points >= 3.15) return 'B+';
  if (points >= 2.65) return 'B';
  if (points >= 2.15) return 'C+';
  if (points >= 1.65) return 'C';
  if (points >= 1.15) return 'D+';
  if (points >= 0.5) return 'D';
  return 'F';
}

/**
 * Convert a team's average value score to a draft grade.
 * Mirrors backend team_draft_analytics.get_draft_grade exactly — use the
 * backend's `draft_grade` field when present; this exists for cases where
 * only the raw avg_value_score is available.
 */
export function computeDraftGrade(avgValueScore: number): LetterGrade {
  if (avgValueScore > 50) return 'A+';
  if (avgValueScore > 25) return 'A';
  if (avgValueScore > 10) return 'B+';
  if (avgValueScore > 0) return 'B';
  if (avgValueScore > -10) return 'C+';
  if (avgValueScore > -25) return 'C';
  if (avgValueScore > -50) return 'D';
  return 'F';
}

/**
 * Grade from a Pick Score (0–100, banded to 5s): the percentage of
 * same-position players still on the board that the pick finished ahead of.
 * Null score (too few alternatives to judge) → null grade.
 */
export function pickScoreToGrade(score: number | null | undefined): LetterGrade | null {
  if (score === null || score === undefined) return null;
  if (score >= 100) return 'A+';
  if (score >= 90) return 'A';
  if (score >= 80) return 'B+';
  if (score >= 70) return 'B';
  if (score >= 60) return 'C+';
  if (score >= 45) return 'C';
  if (score >= 35) return 'D+';
  if (score >= 20) return 'D';
  return 'F';
}

/**
 * Grade a single pick from its value score (actual points minus
 * round-expected points — mirrors the backend's per-pick value bands).
 */
export function computePickGrade(valueScore: number): LetterGrade {
  if (valueScore > 100) return 'A+';
  if (valueScore > 50) return 'A';
  if (valueScore > 20) return 'B+';
  if (valueScore > 0) return 'B';
  if (valueScore > -25) return 'C';
  if (valueScore > -50) return 'D+';
  if (valueScore > -100) return 'D';
  return 'F';
}

/**
 * Luck grade from standings data: schedule victims (high points-against,
 * losses despite scoring) grade low on luck; schedule beneficiaries high.
 * All inputs come from the standings response.
 */
export function computeLuckGrade(params: {
  wins: number;
  losses: number;
  pointsFor: number;
  pointsAgainst: number;
  leaguePointsFor: number[]; // every team's PF, for percentile context
  leaguePointsAgainst: number[];
}): LetterGrade {
  const { wins, losses, pointsFor, pointsAgainst, leaguePointsFor, leaguePointsAgainst } =
    params;
  const games = wins + losses;
  if (games === 0 || leaguePointsFor.length === 0) return 'C';

  const pfPercentile = percentile(leaguePointsFor, pointsFor);
  const paPercentile = percentile(leaguePointsAgainst, pointsAgainst);
  const winPct = wins / games;

  // Luck = record outperforming scoring profile.
  // Positive: wins beyond what PF earns. Negative: schedule victimhood (high PA).
  const expectedWinPct = pfPercentile;
  const luckScore = (winPct - expectedWinPct) * 0.7 + (0.5 - paPercentile) * 0.3;

  // luckScore roughly in [-0.7, 0.7]; map to grade points around C+.
  return pointsToGrade(2.3 + luckScore * 3.2);
}

/**
 * Overall season grade: weighted composite of draft skill, results, and
 * luck-adjustment. winPercentile is this team's rank-based percentile (1 = best).
 */
export function computeSeasonGrade(params: {
  draftGrade: LetterGrade;
  winPercentile: number; // 0..1, 1 = top of standings
  luckGrade: LetterGrade;
}): LetterGrade {
  const { draftGrade, winPercentile, luckGrade } = params;
  const draftPts = gradeToPoints(draftGrade);
  const recordPts = winPercentile * 4.3;
  // Luck inverts as an adjustment: lucky teams get docked slightly, unlucky
  // teams get credit — the grade rewards skill, not schedule.
  const luckAdjustment = (2.15 - gradeToPoints(luckGrade)) * 0.2;
  return pointsToGrade(draftPts * 0.4 + recordPts * 0.6 + luckAdjustment);
}

/** Percentile (0..1) of `value` within `values`. */
export function percentile(values: number[], value: number): number {
  if (values.length <= 1) return 0.5;
  const below = values.filter((v) => v < value).length;
  return below / (values.length - 1);
}

/** Type guard for strings coming off the wire. */
export function isLetterGrade(value: string | undefined | null): value is LetterGrade {
  return GRADE_ORDER.includes(value as LetterGrade);
}
